import { ref, readonly } from 'vue'
import { useAiAssistantStore } from '@/stores/aiAssistant'

const base = import.meta.env.VITE_API_URL

// Voice runs through our backend WebSocket proxy. The browser never receives
// the Gemini key, model, prompt, tools or the upstream Google URL — it only
// streams audio and tool responses. The proxy path mirrors the HTTP API base
// (`<base>/ai/live-ws`) with the http(s) scheme swapped to ws(s).
function buildLiveWsUrl(lang) {
    const b = String(base || '').replace(/\/+$/, '')
    let httpUrl
    if (/^https?:\/\//i.test(b)) {
        httpUrl = `${b}/ai/live-ws`
    } else {
        const prefix = b ? (b.startsWith('/') ? b : `/${b}`) : ''
        httpUrl = `${window.location.origin}${prefix}/ai/live-ws`
    }
    const wsUrl = httpUrl.replace(/^http/i, 'ws')
    return lang ? `${wsUrl}?lang=${encodeURIComponent(lang)}` : wsUrl
}

const INPUT_SAMPLE_RATE = 16000
const OUTPUT_SAMPLE_RATE = 24000

const WORKLET_CODE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
    process(inputs) {
        const ch = inputs[0]?.[0];
        if (ch && ch.length) this.port.postMessage(ch);
        return true;
    }
}
registerProcessor('pcm-capture', PcmCaptureProcessor);
`

export function useGeminiLive() {
    const state = ref('idle')
    const aiSpeaking = ref(false)
    const userSpeaking = ref(false)
    const transcriptLines = ref([])
    const error = ref(null)
    const pendingOrder = ref(null)

    let currentAiText = ''

    let ws = null
    let audioCtx = null
    let mediaStream = null
    let sourceNode = null
    let workletNode = null
    let playbackCtx = null
    let nextPlayTime = 0

    function float32ToPcm16(float32) {
        const buf = new ArrayBuffer(float32.length * 2)
        const view = new DataView(buf)
        for (let i = 0; i < float32.length; i++) {
            const s = Math.max(-1, Math.min(1, float32[i]))
            view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
        }
        return new Uint8Array(buf)
    }

    function arrayBufferToBase64(buffer) {
        let binary = ''
        const bytes = new Uint8Array(buffer)
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
        return btoa(binary)
    }

    function base64ToArrayBuffer(b64) {
        const bin = atob(b64)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        return bytes.buffer
    }

    async function startMicrophone() {
        mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: { sampleRate: INPUT_SAMPLE_RATE, channelCount: 1, echoCancellation: true, noiseSuppression: true },
        })

        audioCtx = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE })

        const blob = new Blob([WORKLET_CODE], { type: 'application/javascript' })
        const url = URL.createObjectURL(blob)
        await audioCtx.audioWorklet.addModule(url)
        URL.revokeObjectURL(url)

        sourceNode = audioCtx.createMediaStreamSource(mediaStream)
        workletNode = new AudioWorkletNode(audioCtx, 'pcm-capture')
        workletNode.port.onmessage = (e) => {
            if (state.value !== 'active' || !ws || ws.readyState !== WebSocket.OPEN) return
            const pcm = float32ToPcm16(e.data)
            const b64 = arrayBufferToBase64(pcm.buffer)
            ws.send(JSON.stringify({ realtimeInput: { audio: { data: b64, mimeType: 'audio/pcm;rate=16000' } } }))
        }
        sourceNode.connect(workletNode)
        workletNode.connect(audioCtx.destination)
    }

    function stopMicrophone() {
        workletNode?.disconnect()
        sourceNode?.disconnect()
        audioCtx?.close().catch(() => {})
        mediaStream?.getTracks().forEach(t => t.stop())
        workletNode = null
        sourceNode = null
        audioCtx = null
        mediaStream = null
    }

    function ensurePlaybackCtx() {
        if (!playbackCtx || playbackCtx.state === 'closed') {
            playbackCtx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE })
            nextPlayTime = 0
        }
        return playbackCtx
    }

    function enqueueAudio(pcmBase64) {
        const ctx = ensurePlaybackCtx()
        const raw = base64ToArrayBuffer(pcmBase64)
        const int16 = new Int16Array(raw)
        const float32 = new Float32Array(int16.length)
        for (let i = 0; i < int16.length; i++) {
            float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7FFF)
        }

        const buffer = ctx.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE)
        buffer.copyToChannel(float32, 0)

        const source = ctx.createBufferSource()
        source.buffer = buffer

        const now = ctx.currentTime
        const startAt = Math.max(now + 0.02, nextPlayTime)
        nextPlayTime = startAt + buffer.duration

        source.connect(ctx.destination)
        source.start(startAt)
    }

    function stopPlayback() {
        playbackCtx?.close().catch(() => {})
        playbackCtx = null
        nextPlayTime = 0
    }

    async function handleMessage(evt) {
        let raw = evt.data
        if (raw instanceof Blob) raw = await raw.text()
        let msg
        try { msg = JSON.parse(raw) } catch { return }

        if (msg.setupComplete) {
            state.value = 'active'
            return
        }

        if (msg.toolCall) {
            handleToolCall(msg.toolCall)
            return
        }

        if (msg.toolCallCancellation) {
            pendingOrder.value = null
            return
        }

        const sc = msg.serverContent
        if (!sc) return

        if (sc.interrupted) {
            aiSpeaking.value = false
            stopPlayback()
            ensurePlaybackCtx()
            return
        }

        if (sc.outputTranscription?.text) {
            currentAiText += sc.outputTranscription.text
            const lastIdx = transcriptLines.value.length - 1
            if (lastIdx >= 0 && transcriptLines.value[lastIdx].role === 'ai') {
                transcriptLines.value[lastIdx].text = currentAiText
            } else {
                transcriptLines.value.push({ role: 'ai', text: currentAiText })
            }
        }
        if (sc.inputTranscription?.text) {
            userSpeaking.value = true
            setTimeout(() => { userSpeaking.value = false }, 600)
        }

        const parts = sc.modelTurn?.parts
        if (parts) {
            for (const part of parts) {
                if (part.inlineData?.mimeType?.startsWith('audio/')) {
                    aiSpeaking.value = true
                    enqueueAudio(part.inlineData.data)
                }
            }
        }

        if (sc.turnComplete) {
            aiSpeaking.value = false
            currentAiText = ''
        }
    }

    async function handleToolCall(toolCall) {
        const store = useAiAssistantStore()
        const responses = []

        for (const fc of toolCall.functionCalls) {
            if (fc.name === 'create_order') {
                try {
                    const result = await store.confirmOrder(fc.args)
                    responses.push({
                        id: fc.id,
                        name: fc.name,
                        response: result.success
                            ? { success: true, orderId: result.orderId, message: `Замовлення #${result.orderId} записано` }
                            : { success: false, error: result.error },
                    })
                    if (result.success) {
                        store.orderSuccess = `Замовлення #${result.orderId} записано!`
                        setTimeout(() => { store.orderSuccess = null }, 4000)
                    } else {
                        store.orderError = result.error
                        setTimeout(() => { store.orderError = null }, 5000)
                    }
                } catch (err) {
                    responses.push({
                        id: fc.id,
                        name: fc.name,
                        response: { success: false, error: err.message },
                    })
                }
            }
        }

        if (responses.length && ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
                toolResponse: { functionResponses: responses },
            }))
        }
    }

    async function startCall() {
        if (state.value !== 'idle') return
        state.value = 'connecting'
        error.value = null
        transcriptLines.value = []
        currentAiText = ''
        pendingOrder.value = null

        try {
            const lang = document.documentElement.lang || 'uk'

            await startMicrophone()

            // Backend proxy builds the setup (model, prompt, tools, voice) and
            // opens the upstream connection itself, so there is no setup to send.
            ws = new WebSocket(buildLiveWsUrl(lang))

            ws.onmessage = handleMessage

            ws.onerror = () => {
                error.value = 'Помилка з\'єднання з AI'
                endCall()
            }

            ws.onclose = (e) => {
                if (state.value === 'active' || state.value === 'connecting') {
                    error.value = e.reason || `З'єднання закрито (${e.code})`
                    state.value = 'idle'
                }
                aiSpeaking.value = false
            }
        } catch (err) {
            error.value = err.response?.data?.error || 'Не вдалося почати дзвінок'
            state.value = 'idle'
            stopMicrophone()
        }
    }

    function endCall() {
        if (ws) {
            ws.onclose = null
            ws.close()
            ws = null
        }
        stopMicrophone()
        stopPlayback()
        state.value = 'idle'
        aiSpeaking.value = false
        userSpeaking.value = false
    }

    return {
        state: readonly(state),
        aiSpeaking: readonly(aiSpeaking),
        userSpeaking: readonly(userSpeaking),
        transcriptLines: readonly(transcriptLines),
        error: readonly(error),
        pendingOrder: readonly(pendingOrder),
        startCall,
        endCall,
    }
}
